import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { EventsSegments, IEventSegment } from '../../../model/events.model';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { debounceTime, map, shareReplay, startWith } from 'rxjs/operators';
import { Helpers } from '../../../../../core/shared/helpers';

interface IVisibleSegment extends IEventSegment {
  visible: boolean;
}
@Component({
    selector: 'app-segment-input',
    templateUrl: './segment-input.component.html',
    styleUrls: ['./segment-input.component.scss'],
    standalone: false
})
export class SegmentInputComponent implements OnInit {
  @Input() set segmentsList(value: IEventSegment[]) {
    this._segments.next(value);
  }

  @Input() formGroup: UntypedFormGroup;
  @Input() multi = true;
  @Input() e2eTag = '';
  @Input() appearance = 'outline';
  @Input() suffix = false;
  @Input() enableGlobalSelect = true;
  @Input() showLabel = true;
  @Input() searchEnabled = true;
  @Input() className = '';
  @Input() helpText = '';
  @Input() label = '';
  @Input() placeholder = '';
  @Input() control = 'segments';
  @Input() disabledOptions = [];
  globalValue = EventsSegments.GLOBAL;
  _segments: BehaviorSubject<IEventSegment[]> = new BehaviorSubject([]);

  searchTerm = new FormControl<string>('');
  visibleSegments$: Observable<IVisibleSegment[]>;
  @ViewChild('segment', {static: true}) segment: MatSelect

  constructor() {}

  get segments(): AbstractControl<string[] | string> {
    return this.formGroup?.get('segments');
  }

  ngOnInit(): void {
    this.visibleSegments$ = combineLatest([
      this.searchTerm.valueChanges.pipe(startWith('')),
      this._segments.asObservable()
    ]).pipe(
      debounceTime(0),
      map(([searchTerm, segments]) => segments.map(segment => ({
        ...segment,
        visible: segment.name.toLowerCase().includes(searchTerm.toLowerCase())
      }))),
      shareReplay(1)
    );
  }

  reset($event: Event) {
    $event.stopPropagation();
    this.segments.patchValue('');
  }

  get hasValue() {
    return this.segments.value && this.segments.value.length;
  }

  toggleSelection($change: MatSelectChange): void {
    if (this.multi && this.enableGlobalSelect) {
      Helpers.resetGlobalSegment($change, () => {
        $change.source?.options.forEach(option => {
          if (option.value === this.globalValue) {
            option.select();
          } else {
            option.deselect();
          }
        })
      });
    }
    if (!this.multi) {
      this.segment.close();
    }
  }

  selectNone() {
    this.segment.options.forEach(option => option.deselect());
    this.segments.patchValue([]);
  }

  deselectAll() {
    this.handleMultiple(segments => {
      // deselect visible segments
      this.segments.patchValue((this.segments.value as string[]).filter(segment => !segments.includes(segment)));
    });
  }

  selectAll() {
    this.handleMultiple(segments => {
      // upsert visible segments to form control
      const currentSegments = this.segments.value;
      const newSegments = Array.from(new Set([...currentSegments, ...segments])).filter(segment => segment !== this.globalValue);
      this.segments.patchValue(newSegments);
    });
  }

  private async handleMultiple(callback: (segments: string[]) => void) {
   // get list of visible segments
   const segments = await firstValueFrom(this.visibleSegments$.pipe(
      map(segments => segments.filter(segment => segment.visible).map(segment => segment.id))
    ));
    // deselect global segment
    if (this.enableGlobalSelect && this.segment.options.first.active) {
      this.segment.options.first.deselect();
    }
    callback(segments);
  }
}
